import { CSSProperties, FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { NextExportPreview, NextPurchaseConflict } from "../../services/ServiceNextProject";
import StyledCheckbox from "../../components/shared/StyledCheckbox/StyledCheckbox";
import BtButton from "../bt-button/bt-button";
import TooltipContainer from "../../components/shared/TooltipContainer/TooltipContainer";
import FilterPill from "../../components/shared/FilterPill/FilterPill";
import { Loading } from "../../components/shared";
import styles from './NextConflictSection.module.scss';
import {
  ConflictSelection,
  buildFullSelection,
  conflictFieldCounts,
  countSelectedFields,
  purchaseSelectionState,
  selectionKey,
  setPurchaseSelected,
  setPurchasesSelected,
  toggleField,
} from "./nextConflictSelection";

// 'export' pushes Accurator values to Next (current = Next side);
// 'import' pulls Next values into Accurator (current = Accurator side).
export type ConflictDirection = 'export' | 'import';

// Collapsed purchase rows show at most this many field chips; the rest are
// summarized in a +N chip with a tooltip (same pattern as the bid-overview
// version chips).
const MAX_VISIBLE_FIELD_CHIPS = 3;

type Props = {
  preview: NextExportPreview | null,
  loading: boolean,
  direction: ConflictDirection,
  expanded: boolean,
  onToggleExpanded: () => void,
  selection: ConflictSelection,
  onSelectionChange: (selection: ConflictSelection) => void,
}

const Chevron: FC<{ open?: boolean }> = ({ open }) => (
  <span className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`}>
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6"></path></svg>
  </span>
);

const RightArrow: FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"></path><path d="M13 6l6 6-6 6"></path></svg>
);

export const useConflictFieldLabels = () => {
  const [t] = useTranslation('projects');
  return (field: string) => ({
    budget: t('exportNextProjectModal.conflicts.fields.budget', 'Budget'),
    purchaseGoalPercent: t('exportNextProjectModal.conflicts.fields.purchaseGoalPercent', 'Inköpsmål (%)'),
    quotationSentBy: t('exportNextProjectModal.conflicts.fields.quotationSentBy', 'Förfrågan skickas senast'),
    answersLatestBy: t('exportNextProjectModal.conflicts.fields.answersLatestBy', 'Svar senast'),
    purchaseCompleted: t('exportNextProjectModal.conflicts.fields.purchaseCompleted', 'Inköp klart'),
    productionStartsAt: t('exportNextProjectModal.conflicts.fields.productionStartsAt', 'Leveransstart'),
    productionEndsAt: t('exportNextProjectModal.conflicts.fields.productionEndsAt', 'Leveransslut'),
  }[field] ?? field);
};

// Per-field dot colours for the filter badges — same mechanism as the to-do
// view's activity dots (activityMeta.typeDotStyle). An unmapped field name
// degrades to a grey dot rather than throwing.
const FIELD_DOT_COLORS: Record<string, string> = {
  budget: '#3b82f6',
  purchaseGoalPercent: '#8b5cf6',
  quotationSentBy: '#f59e0b',
  answersLatestBy: '#14b8a6',
  purchaseCompleted: '#22c55e',
  productionStartsAt: '#f97316',
  productionEndsAt: '#ef4444',
};

const fieldDotStyle = (field: string): CSSProperties =>
  ({ '--dot-color': FIELD_DOT_COLORS[field] ?? '#94a3b8' } as CSSProperties);

const formatConflictValue = (value: string | number | null) => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'number') return value.toLocaleString('sv-SE');
  return value;
};

const NextConflictSection: FC<Props> = ({ preview, loading, direction, expanded, onToggleExpanded, selection, onSelectionChange }) => {
  const [t] = useTranslation('projects');
  const fieldLabel = useConflictFieldLabels();
  const [expandedPurchases, setExpandedPurchases] = useState<Set<number>>(new Set());
  const [activeFilters, setActiveFilters] = useState<Set<string>>(new Set());

  if (loading) {
    return (
      <div className={`${styles.section} ${styles.loadingRow}`}>
        <Loading type="inline" color="#ff5e15" size="18px" />
        <span className={styles.loadingText}>
          {t('exportNextProjectModal.conflicts.loading', 'Jämför värden med Next…')}
        </span>
      </div>
    );
  }

  const hasContent = preview && (
    preview.conflicts.length > 0 || preview.willBeRecreated.length > 0 || (preview.missingAccounts?.length ?? 0) > 0
  );
  if (!hasContent) return null;

  const isImport = direction === 'import';
  const { conflicts, willBeRecreated } = preview;
  const totalFields = preview.totalConflictFields;
  const otherItemsCount = willBeRecreated.length + (preview.missingAccounts?.length ?? 0);
  const fieldCounts = conflictFieldCounts(conflicts);
  // The conflicts as the filter shows them: hidden fields stripped, purchases
  // with nothing visible dropped. Scoping the master/purchase checkboxes to
  // this structure makes every selection helper "visible-only" for free.
  const visibleConflicts = activeFilters.size === 0
    ? conflicts
    : conflicts
      .map(purchase => ({ ...purchase, fields: purchase.fields.filter(f => activeFilters.has(f.field)) }))
      .filter(purchase => purchase.fields.length > 0);
  const visibleSelectedFields = countSelectedFields(selection, visibleConflicts);
  const visibleTotalFields = visibleConflicts.reduce((sum, c) => sum + c.fields.length, 0);

  // Filtering a field out also DESELECTS it — a hidden field must never ride
  // along into the update payload. Conversely, ACTIVATING a badge selects all
  // of its fields. Types that stay active keep their state, so a manual
  // uncheck survives toggling other badges.
  const applyFilters = (nextFilters: Set<string>, addedType?: string) => {
    setActiveFilters(nextFilters);
    if (nextFilters.size === 0) {
      // "Alla fält": show everything and select everything, matching the
      // modal's initial state.
      onSelectionChange(buildFullSelection(conflicts));
      return;
    }
    const next: ConflictSelection = {};
    for (const purchase of conflicts) {
      for (const conflictField of purchase.fields) {
        if (!nextFilters.has(conflictField.field)) continue;
        const key = selectionKey(purchase.nextPurchaseId, conflictField.field);
        if (conflictField.field === addedType || selection[key]) next[key] = true;
      }
    }
    onSelectionChange(next);
  };
  const toggleFieldFilter = (field: string) => {
    const next = new Set(activeFilters);
    let addedType: string | undefined;
    if (next.has(field)) {
      next.delete(field);
    } else {
      next.add(field);
      addedType = field;
    }
    applyFilters(next, addedType);
  };

  const togglePurchaseExpanded = (nextPurchaseId: number) => {
    const next = new Set(expandedPurchases);
    if (next.has(nextPurchaseId)) next.delete(nextPurchaseId); else next.add(nextPurchaseId);
    setExpandedPurchases(next);
  };

  const currentValue = (f: { accValue: string | number | null, nextValue: string | number | null }) =>
    isImport ? f.accValue : f.nextValue;
  const incomingValue = (f: { accValue: string | number | null, nextValue: string | number | null }) =>
    isImport ? f.nextValue : f.accValue;

  const renderFieldGrid = (purchase: NextPurchaseConflict) => (
    <div className={styles.fieldGrid}>
      <div className={styles.fieldGridHeader}>
        <div></div>
        <div>{t('exportNextProjectModal.conflicts.field', 'Fält')}</div>
        <div className={styles.nextHeader}>
          {isImport
            ? t('updateFromNextModal.conflicts.nowInAccurator', 'Nu i Accurator')
            : t('exportNextProjectModal.conflicts.nowInNext', 'Nu i Next')}
        </div>
        <div></div>
        <div>
          {isImport
            ? t('updateFromNextModal.conflicts.fromNext', 'Från Next')
            : t('exportNextProjectModal.conflicts.fromAccurator', 'Från Accurator')}
        </div>
      </div>
      {purchase.fields.map(conflictField => {
        const isSelected = !!selection[selectionKey(purchase.nextPurchaseId, conflictField.field)];
        const visible = activeFilters.size === 0 || activeFilters.has(conflictField.field);
        return (
          <div key={conflictField.field} className={`${styles.collapse} ${visible ? styles.collapseOpen : ''}`}>
            <div className={styles.collapseInner}>
              <div className={`${styles.fieldRow} ${isSelected ? styles.fieldRowSelected : styles.fieldRowUnselected}`}>
                <StyledCheckbox
                  checked={isSelected}
                  onChange={() => onSelectionChange(toggleField(selection, purchase.nextPurchaseId, conflictField.field))}
                  className={styles.checkboxFlush}
                />
                <div className={styles.fieldName}>{fieldLabel(conflictField.field)}</div>
                <div className={styles.nextValue}><span>{formatConflictValue(currentValue(conflictField))}</span></div>
                <div className={styles.arrow}><RightArrow /></div>
                <div className={styles.accValue}><span>{formatConflictValue(incomingValue(conflictField))}</span></div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderPurchaseRow = (purchase: NextPurchaseConflict) => {
    // Scope the row checkbox to the fields the filter shows: hidden fields
    // are always unselected, so judging or toggling them would make 'all'
    // unreachable and re-check fields the filter just pruned.
    const scopedPurchase = activeFilters.size === 0
      ? purchase
      : { ...purchase, fields: purchase.fields.filter(f => activeFilters.has(f.field)) };
    const state = purchaseSelectionState(selection, scopedPurchase);
    const isExpanded = expandedPurchases.has(purchase.nextPurchaseId);
    // A purchase with no field matching the active badges is omitted
    // entirely, animated by the same collapse primitive as its field rows.
    const purchaseVisible = scopedPurchase.fields.length > 0;
    return (
      <div key={purchase.nextPurchaseId} className={`${styles.collapse} ${purchaseVisible ? styles.collapseOpen : ''}`}>
        <div className={styles.collapseInner}>
        <div className={styles.purchaseRow}>
        <div
          className={`${styles.purchaseHead} ${isExpanded ? styles.purchaseHeadExpanded : ''}`}
          onClick={() => togglePurchaseExpanded(purchase.nextPurchaseId)}
        >
          <span onClick={e => e.stopPropagation()} className={styles.checkboxCell}>
            <StyledCheckbox
              checked={state !== 'none'}
              dash={state === 'some'}
              onChange={() => onSelectionChange(setPurchaseSelected(selection, scopedPurchase, state !== 'all'))}
              className={styles.checkboxFlush}
            />
          </span>
          <Chevron open={isExpanded} />
          <div className={styles.purchaseName}>
            {purchase.name}
            {purchase.accountNumber && <div className={styles.purchaseRef}>{purchase.accountNumber}</div>}
          </div>
          <div className={`${styles.fieldChips} ${isExpanded ? styles.fieldChipsHidden : ''}`}>
              {purchase.fields.slice(0, MAX_VISIBLE_FIELD_CHIPS).map(conflictField => (
                <span key={conflictField.field} className={styles.fieldChip}>
                  <span className={styles.fieldChipDot} style={fieldDotStyle(conflictField.field)} />
                  {fieldLabel(conflictField.field)}
                </span>
              ))}
              {purchase.fields.length > MAX_VISIBLE_FIELD_CHIPS && (

                <TooltipContainer renderReferenceComponent={(className, ref) => (
                  <span ref={ref} className={`${styles.fieldChip} ${className}`}>
                    +{purchase.fields.length - MAX_VISIBLE_FIELD_CHIPS}
                  </span>
                )}>
                  <span className={`tooltip-text ${styles.moreFieldsTooltip}`}>
                    {t(
                      'exportNextProjectModal.conflicts.moreFields',
                      'Fler fält som skiljer sig: {{fields_list}}',
                      { fields: purchase.fields.slice(MAX_VISIBLE_FIELD_CHIPS).map(f => fieldLabel(f.field)) },
                    )}
                  </span>
                </TooltipContainer>
              )}
            </div>
        </div>
        {/* Same collapse primitive as the panel itself, so expanding a
            purchase animates the height instead of snapping; the delayed
            visibility flip keeps collapsed field checkboxes out of the tab
            order. */}
        <div className={`${styles.collapse} ${isExpanded ? styles.collapseOpen : ''}`}>
          <div className={styles.collapseInner}>
            {renderFieldGrid(purchase)}
          </div>
        </div>
        </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.section}>
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          {t(
            'exportNextProjectModal.conflicts.headline',
            '{{count}} ändringar upptäckta',
            { count: totalFields > 0 ? totalFields : otherItemsCount },
          )}
        </div>
        {conflicts.length > 0 && (
          <BtButton color="white" size="small" onClick={onToggleExpanded}>
            {expanded
              ? t('exportNextProjectModal.conflicts.collapse', 'Dölj')
              : t('exportNextProjectModal.conflicts.review', 'Granska & välj')}
          </BtButton>
        )}
      </div>

      {conflicts.length > 0 && (
        <div className={`${styles.collapse} ${!expanded ? styles.collapseOpen : ''}`}>
          {/* collapsedBody paints the summary's margin-top area yellow — a
              margin is transparent, and without it the white section
              background shows as a band between header and summary. */}
          <div className={`${styles.collapseInner} ${styles.collapsedBody}`}>
            <div className={styles.summaryText}>
              {t(
                'exportNextProjectModal.conflicts.summary',
                '{{conflictPurchases}} inköp påverkas, {{totalFields}} fält uppdateras',
                { conflictPurchases: conflicts.length, totalFields },
              )}
            </div>
          </div>
        </div>
      )}

      {conflicts.length > 0 && (
        <div className={`${styles.collapse} ${expanded ? styles.collapseOpen : ''}`}>
          <div className={styles.collapseInner}>
          <div className={styles.chipRow}>
            <FilterPill
              active={activeFilters.size === 0}
              onClick={() => applyFilters(new Set())}
              label={t('exportNextProjectModal.conflicts.allFields', 'Alla fält')}
              count={totalFields}
            />
            {fieldCounts.map(({ field, count }) => (
              <FilterPill
                key={field}
                active={activeFilters.has(field)}
                onClick={() => toggleFieldFilter(field)}
                dotStyle={fieldDotStyle(field)}
                label={fieldLabel(field)}
                count={count}
              />
            ))}
          </div>
          <div className={styles.listHeader}>
            <label onClick={e => e.stopPropagation()}>
              <StyledCheckbox
                checked={visibleSelectedFields > 0}
                dash={visibleSelectedFields > 0 && visibleSelectedFields < visibleTotalFields}
                onChange={() => onSelectionChange(setPurchasesSelected(selection, visibleConflicts, visibleSelectedFields < visibleTotalFields))}
                className={styles.checkboxFlush}
              />
              {t('exportNextProjectModal.conflicts.purchases', 'Inköp')}
            </label>
            <div className={styles.listHeaderRight}>
              {t('exportNextProjectModal.conflicts.fieldsInConflict', 'Fält som skiljer sig')}
            </div>
          </div>
          <div className={styles.purchaseList}>
            {conflicts.map(renderPurchaseRow)}
          </div>
          </div>
        </div>
      )}

      {willBeRecreated.length > 0 && (
        <div className={styles.recreateInfo}>
          {t(
            'exportNextProjectModal.conflicts.willBeRecreated',
            'Följande inköp finns inte längre i Next och kommer att skapas igen: {{names_list}}',
            { names: willBeRecreated },
          )}
        </div>
      )}
      {(preview.missingAccounts?.length ?? 0) > 0 && (
        <div className={styles.recreateInfo}>
          {isImport
            ? t(
              'updateFromNextModal.conflicts.missingAccounts',
              'Kategorier saknas för följande konton och inköpen kan därför inte importeras: {{names_list}}',
              { names: preview.missingAccounts },
            )
            : t(
              'exportNextProjectModal.conflicts.missingAccounts',
              'Följande inköp finns inte längre i Next och kan inte återskapas eftersom kontokoden saknas: {{names_list}}',
              { names: preview.missingAccounts },
            )}
        </div>
      )}
    </div>
  );
};

export default NextConflictSection;
